import { AlertTriangle, Copy, Info, Minus, Plus, RefreshCw, Snowflake } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import type { CardWalletsTranslations } from "@/shared/lang/interfaces/cardWallets";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import type { WalletEntity } from "@/shared/types/wallet.entity";
import { colocaToast } from "@/shared/utils/toast";

import type { AdjustDirection, CardWalletIneligibility } from "../types/cardWallets.types";
import { ADJUST_CURRENCY, shortWalletId } from "../utils/cardWalletAdjust";
import { CardWalletEligibilityNotice } from "./CardWalletEligibilityNotice";

type StatusView = {
  labelKey: keyof CardWalletsTranslations;
  variant: "success" | "warning" | "danger" | "neutral";
  icon?: typeof Snowflake;
  withDot?: boolean;
};

const STATUS_VIEWS: Record<string, StatusView> = {
  ACTIVE: { labelKey: "wallet_status_active", variant: "success", withDot: true },
  FROZEN: { labelKey: "wallet_status_frozen", variant: "warning", icon: Snowflake },
  SUSPENDED: { labelKey: "wallet_status_suspended", variant: "danger" },
  CLOSED: { labelKey: "wallet_status_closed", variant: "neutral" },
};

export interface CardWalletSummaryProps {
  wallet: WalletEntity | null;
  /** Available balance as a decimal string, or `null` while it is unknown. */
  balance: string | null;
  pending: string | null;
  isLoading: boolean;
  error: string | null;
  /** Set once the card program or the wallet itself rules the organization out. */
  ineligibility: CardWalletIneligibility | null;
  /** The read that resolved this wallet is what created it. Decided at fetch time, never during render. */
  justCreated: boolean;
  /** `false` hides both actions — the operator may read, not write. */
  canAdjust: boolean;
  onAdjust: (direction: AdjustDirection) => void;
  onRetry: () => void;
}

/**
 * The organization's card wallet: what it holds, what state it is in, and the two ways to move it.
 *
 * The actions stay enabled on a frozen or suspended wallet on purpose — nothing in
 * `adjustCryptomateFundingWalletBalance` checks `wallet.status`, and a wallet left suspended by a failed
 * provisioning is exactly the case an operator opens this screen for. Disabling them would invent a rule the
 * backend does not have and hide a legitimate correction.
 */
export function CardWalletSummary({
  wallet,
  balance,
  pending,
  isLoading,
  error,
  ineligibility,
  justCreated,
  canAdjust,
  onAdjust,
  onRetry,
}: CardWalletSummaryProps) {
  const { t } = useTranslations("cardWallets");

  if (isLoading && !wallet) {
    return (
      <div
        aria-busy="true"
        aria-label={t("wallet_loading")}
        className={cn(colocaSurfaceVariants({ variant: "panel", padding: "none" }), "overflow-hidden")}
      >
        <div className="grid lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="p-6">
            <ColocaSkeleton className="h-28 w-full rounded-xl" />
          </div>
          <div className="flex flex-col gap-2 border-t border-border p-6 lg:border-l lg:border-t-0">
            <ColocaSkeleton className="h-11 w-full rounded-md" />
            <ColocaSkeleton className="h-11 w-full rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  // Without the balance the review step could not state the before and after, and a review that cannot state
  // the consequence is not a review — so the actions are withheld rather than shown and then refused.
  if (error && !wallet) {
    return (
      <div className={cn(colocaSurfaceVariants({ variant: "panel", padding: "lg" }))}>
        <ColocaAlert
          variant="destructive"
          icon={AlertTriangle}
          heading={t("wallet_error_title")}
          text={t("wallet_error_body")}
          actions={
            <ColocaButton type="button" variant="default" size="sm" icon={RefreshCw} onClick={onRetry}>
              {t("action_retry")}
            </ColocaButton>
          }
        />
      </div>
    );
  }

  // Ruled out by the card program alone: the wallet was deliberately never read (that read provisions one).
  if (!wallet && ineligibility) {
    return (
      <div className={cn(colocaSurfaceVariants({ variant: "panel", padding: "lg" }))}>
        <CardWalletEligibilityNotice reason={ineligibility} />
      </div>
    );
  }

  if (!wallet) return null;

  const status = STATUS_VIEWS[String(wallet.status).toUpperCase()] ?? STATUS_VIEWS.CLOSED;
  const StatusIcon = status.icon;
  const showsStatusNotice = String(wallet.status).toUpperCase() !== "ACTIVE";
  const actionsVisible = canAdjust && !ineligibility;

  return (
    <section
      aria-label={t("wallet_balance_label")}
      className={cn(colocaSurfaceVariants({ variant: "panel", padding: "none" }), "overflow-hidden")}
    >
      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col gap-3.5 p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("wallet_balance_label")}
            </span>
            <ColocaStatusPill variant={status.variant} icon={StatusIcon} withDot={status.withDot}>
              {t(status.labelKey)}
            </ColocaStatusPill>
          </div>

          {balance === null ? (
            <ColocaSkeleton className="h-9 w-48" />
          ) : (
            <ColocaAmountDisplay
              size="xl"
              value={balance}
              currency={ADJUST_CURRENCY}
              fractionDigits={2}
              className="text-foreground"
            />
          )}

          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 text-sm text-muted-foreground">
            {pending !== null && (
              <span>
                {t("wallet_pending", {
                  amount: `${new Intl.NumberFormat("es-CO", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(Number(pending))} ${ADJUST_CURRENCY}`,
                })}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              {t("wallet_id_label")}
              <span className="font-mono text-xs text-foreground">{shortWalletId(wallet.id)}</span>
              <ColocaIconButton
                type="button"
                variant="ghost"
                size="sm"
                icon={Copy}
                aria-label={t("wallet_id_copy_action")}
                onClick={() => {
                  void navigator.clipboard?.writeText(wallet.id);
                  colocaToast.success(t("wallet_id_copied"));
                }}
              />
            </span>
          </div>

          {justCreated && <ColocaAlert variant="secondary" icon={Info} text={t("wallet_just_created")} />}
          {showsStatusNotice && (
            <ColocaAlert
              variant="warning"
              icon={AlertTriangle}
              text={t("wallet_status_notice", { status: t(status.labelKey).toLowerCase() })}
            />
          )}
          {ineligibility && <CardWalletEligibilityNotice reason={ineligibility} />}
        </div>

        <div className="flex flex-col justify-center gap-3 border-t border-border p-6 lg:border-l lg:border-t-0">
          {actionsVisible ? (
            <>
              {/* Rule 15: these two labels do not fit side by side on a phone and `ColocaButton` never
                  shrinks, so they stack below `lg` and `w-full` goes on each child, not on the row. */}
              <div className="flex flex-col items-stretch gap-2">
                <ColocaButton
                  type="button"
                  variant="primary"
                  size="lg"
                  icon={Plus}
                  className="h-11 w-full lg:h-10"
                  onClick={() => onAdjust("credit")}
                >
                  {t("action_credit")}
                </ColocaButton>
                <ColocaButton
                  type="button"
                  variant="destructive"
                  size="lg"
                  icon={Minus}
                  className="h-11 w-full lg:h-10"
                  onClick={() => onAdjust("debit")}
                >
                  {t("action_debit")}
                </ColocaButton>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">{t("actions_footnote")}</p>
            </>
          ) : (
            <p className="text-xs leading-relaxed text-muted-foreground">{t("actions_footnote")}</p>
          )}
        </div>
      </div>
    </section>
  );
}
